import { PDFSplit } from "@pdfking/renderer-default"
import { Range } from "@pdfking/tfunc"

import { CastPDFReaderOptions } from "../../files/CastPDFReaderOptions"
import { GenerateFile } from "../../files/GenerateFile"
import { ConvertRectangle } from "../../geometry/ConvertRectangle"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import { ContentType } from "../../types/files/ContentType"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskSplitOptions } from "../../types/tasks/options/TaskSplitOptions"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"

export async function* TaskSplit(
  engine: PdfEngine,
  task: TaskSplitOptions,
  emit: TrackingCallback
): AsyncGenerator<TaskResult> {
  const readerOptions = await CastPDFReaderOptions(
    engine,
    task.options,
    task.source,
    emit
  )

  let page_number = task.start_page - 1
  let page_count = 0

  for await (const { blob, trim_box, media_box } of PDFSplit(
    readerOptions,
    total => {
      page_count = total
      const end_page = Math.min(task.end_page, total)
      void emit({
        name: TrackedTaskName.SPLIT_PDF,
        done: 0,
        total: end_page - task.start_page + 1,
      })
      return Range(task.start_page, end_page)
    }
  )) {
    page_number += 1

    const content = await GenerateFile(engine, [blob], ContentType.PDF, emit)

    yield {
      enum: TaskName.CONTENT_FILE,
      name: "page.pdf",
      content: {
        enum: VirtualFileType.SINGLE_PAGE_PDF,
        content,
        source: task.source,
        page: undefined,
        context: undefined,
        file_meta: {},
        page_number,
        page_count,
        trim_box: ConvertRectangle(task.options, trim_box),
        media_box: ConvertRectangle(task.options, media_box),
      },
    } as TaskResult

    void emit({
      name: TrackedTaskName.SPLIT_PDF,
      done: page_number - task.start_page + 1,
      total: Math.min(task.end_page, page_count) - task.start_page + 1,
    })
  }
}
